import { useState, useEffect, useRef } from 'react';
import { tasksService } from '../services/api';

/**
 * Hook simple para alarmas de tareas - Detecta nuevas tareas asignadas al usuario
 */
export const useTaskAlarms = (currentUser) => {
  const [showAlarm, setShowAlarm] = useState(false);
  const [alarmTask, setAlarmTask] = useState(null);
  const [lastTaskCount, setLastTaskCount] = useState(0);
  const intervalRef = useRef(null);
  const isFirstCheck = useRef(true);

  // Extraer username del currentUser
  const username = currentUser?.user?.username || currentUser?.username;

  useEffect(() => {
    if (!username) {
      console.log('⏸️ Alarmas deshabilitadas - No hay usuario');
      return;
    }

    console.log('🚨 Iniciando sistema de alarmas para:', username);

    // Escuchar evento de nueva tarea creada
    const handleTaskCreated = (event) => {
      console.log('📡 Evento taskCreated recibido:', event.detail);
      const { newTask } = event.detail;
      
      // Si la nueva tarea es para mi, mostrar alarma inmediatamente
      if (newTask.assignedTo === username && newTask.estado === 'programada') {
        console.log('🚨 ¡Nueva tarea para mi detectada inmediatamente!', newTask);
        setAlarmTask(newTask);
        setShowAlarm(true);
        
        // Actualizar contador para evitar duplicados en el polling
        setLastTaskCount(prev => prev + 1);
      }
    };

    window.addEventListener('taskCreated', handleTaskCreated);

    const checkForNewTasks = async () => {
      try {
        // Obtener todas las tareas con timeout corto
        const allTasks = await tasksService.getTasks();
        
        // Filtrar mis tareas (asignadas a mi)
        const myTasks = allTasks.filter(task => 
          task.assignedTo === username && 
          task.estado === 'programada'
        );
        
        // Si es la primera verificación, solo contar
        if (isFirstCheck.current) {
          setLastTaskCount(myTasks.length);
          isFirstCheck.current = false;
          return;
        }
        
        // Si hay más tareas que antes, mostrar alarma
        if (myTasks.length > lastTaskCount) {
          // Obtener la tarea más reciente
          const sortedTasks = myTasks.sort((a, b) => 
            new Date(b.createdAt || b.fechaCreacion) - new Date(a.createdAt || a.fechaCreacion)
          );
          
          const newTask = sortedTasks[0];
          
          // Mostrar alarma
          setAlarmTask(newTask);
          setShowAlarm(true);
          
          // Actualizar contador
          setLastTaskCount(myTasks.length);
        }
        
      } catch (error) {
        // Silenciar errores de timeout para no llenar la consola
        if (!error.message?.includes('timeout')) {
          console.error('Error verificando tareas:', error.message);
        }
      }
    };

    // Primera verificación inmediata
    checkForNewTasks();

    // Verificar cada 30 segundos (antes era 5 segundos - causaba lentitud)
    intervalRef.current = setInterval(checkForNewTasks, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        console.log('⏹️ Sistema de alarmas detenido');
      }
      window.removeEventListener('taskCreated', handleTaskCreated);
    };
  }, [username]);

  const closeAlarm = () => {
    console.log('🔕 Cerrando alarma manualmente');
    setShowAlarm(false);
    setAlarmTask(null);
  };

  return {
    showAlarm,
    alarmTask,
    closeAlarm
  };
};